import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { triggerSelectionHaptic } from '../../../shared/utils/haptics';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';

import { layoutLtrStyle, isRTL } from '../../../shared/i18n/rtl';
import { androidScaleSize } from '../../../shared/theme/androidUiParity';
import { SvgIcon } from '../SvgIcon';
import { AnimatedCount } from './animated-count/AnimatedCount';
import { PlainAnimatedNumber } from './animated-count/PlainAnimatedNumber';
import { PlainCmSlashImperialReadout } from './animated-count/PlainCmSlashImperialReadout';
import { DraggableSlider } from './draggable-slider/DraggableSlider';
import type { WheelPickerFieldProps, WheelPickerProps } from './types';

const DEMO_LINES_AMOUNT = 50;

const isFieldProps = (p: WheelPickerProps): p is WheelPickerFieldProps & WheelPickerProps => {
  return p.valueMin != null && p.valueMax != null && p.onValueChange != null && p.value != null;
};

const triggerSelectionFeedback = () => {
  triggerSelectionHaptic();
};

export const WheelPicker = (props: WheelPickerProps) => {
  const {
    backgroundColor = '#FFF',
    countFontSize,
    countColor,
    gradientAccentColor,
    textDigitWidth = 44,
    textDigitHeight = 80,
    maxDigits,
    simpleReadout = false,
    lineColor = '#c6c6c6',
    bigLineColor = '#c6c6c6',
    scrollableAreaHeight = 150,
    minHeight,
    fillContainer = true,
    showExpandButton = true,
    leadingReadout,
    trailingReadout,
    readoutGroupStyle,
    readoutValueClusterStyle,
    readoutGutterV,
    readoutGutterBottom,
    maxLineHeight = 20,
    minLineHeight = 10,
    lineWidth = 1.5,
    lineCanvasPaddingTop: lineCanvasPaddingTopProp,
    idleSliderArrowsAfterMs,
    idleSliderArrowsColor,
    readoutSplit: readoutSplitProp = 'none',
    captionBelowReadout,
    readoutAlign: readoutAlignProp = 'center',
    scaleMarkerColor,
    readoutLabelNudgeY: readoutLabelNudgeYProp = 0,
    containerStyle,
    testID,
    onSliderGestureActiveChange,
    fixedSpacePerLine,
    bigLineEvery,
    snapSpring,
    metricUnitLabel,
    overscrollTicks,
  } = props;

  const readout_align = readoutAlignProp;
  const is_readout_start = readout_align === 'start';
  const is_readout_between = readout_align === 'between';
  const is_readout_stretch = readout_align === 'start' || readout_align === 'between';
  const has_leading_readout = leadingReadout != null && leadingReadout !== false;
  const readout_label_nudge_style =
    readoutLabelNudgeYProp > 0 ? { transform: [{ translateY: -readoutLabelNudgeYProp }] } : undefined;

  const line_canvas_pad =
    lineCanvasPaddingTopProp != null
      ? lineCanvasPaddingTopProp
      : readoutGutterV != null
        ? 0
        : undefined;

  const embed_style = useMemo(() => {
    if (readoutGutterV == null) {
      return S.embed;
    }
    const padding_bottom = readoutGutterBottom ?? readoutGutterV;
    return [
      S.embed,
      { paddingTop: readoutGutterV, paddingBottom: padding_bottom },
      is_readout_stretch && S.embedStart,
    ];
  }, [readoutGutterV, readoutGutterBottom, is_readout_stretch]);

  const is_field = isFieldProps(props);
  const value_min = is_field ? props.valueMin : 0;
  const value_max = is_field ? props.valueMax : 0;
  const value = is_field ? props.value : 0;

  const snap_value_to_step = useCallback(
    (raw: number) => {
      if (!is_field) {
        return raw;
      }
      return Math.max(value_min, Math.min(value_max, Math.round(raw)));
    },
    [is_field, value_min, value_max],
  );

  const on_value_change_ref = useRef(is_field ? props.onValueChange : undefined);
  useLayoutEffect(() => {
    on_value_change_ref.current = is_field ? props.onValueChange : undefined;
  }, [is_field, is_field ? props.onValueChange : null]);

  const run_field_value = useCallback((v: number) => {
    on_value_change_ref.current?.(v);
  }, []);

  const [is_expanded, setIsExpanded] = useState(false);
  const animated_space_per_line = useDerivedValue<number>(() => {
    if (!showExpandButton) {
      return 20;
    }
    return withSpring(is_expanded ? 50 : 20, {
      dampingRatio: 1,
      duration: 500,
    });
  }, [is_expanded, showExpandButton]);

  const lines_amount = useMemo(
    () => (is_field ? Math.max(1, value_max - value_min + 1) : DEMO_LINES_AMOUNT),
    [is_field, value_min, value_max],
  );

  const effective_value = useMemo(
    () => (is_field ? snap_value_to_step(value) : 0),
    [is_field, value, snap_value_to_step],
  );

  const progress_from_value = useCallback(
    (v: number) => {
      if (!is_field || lines_amount <= 1) {
        return 0;
      }
      const max_idx = lines_amount - 1;
      const idx = Math.min(max_idx, Math.max(0, v - value_min));
      return idx / max_idx;
    },
    [is_field, lines_amount, value_min],
  );

  const user_progress = useSharedValue(0);
  const previous_line_index = useSharedValue(-1);
  const last_haptic_ms = useSharedValue(0);
  const [gesture_active, setGestureActive] = useState(false);
  const gesture_active_ref = useRef(false);

  /** Seed once — controlled value updates must not re-seed the slider mid-interaction. */
  const seed_progress_ref = useRef(is_field ? progress_from_value(effective_value) : null);

  useLayoutEffect(() => {
    if (!is_field || gesture_active_ref.current) {
      return;
    }
    user_progress.value = progress_from_value(effective_value);
  }, [is_field, effective_value, progress_from_value, user_progress]);

  const initial_progress = seed_progress_ref.current;

  const lines_for_worklet = lines_amount;
  const value_min_w = is_field ? value_min : 0;
  const big_line_every = bigLineEvery ?? (is_field ? 10 : 20);
  const slider_space_per_line =
    fixedSpacePerLine != null ? fixedSpacePerLine : showExpandButton ? animated_space_per_line : 20;

  const readout_split = !is_field ? 'none' : (readoutSplitProp ?? 'none');
  const count_digits = is_field ? (maxDigits ?? Math.max(2, String(value_max).length)) : 10;
  const resolved_font = countFontSize ?? 65;
  const imperial_glyph_size = Math.max(18, Math.round(resolved_font * 0.58));
  const dual_left_digits =
    readout_split === 'imperial_from_cm' || readout_split === 'cm_slash_imperial'
      ? 1
      : readout_split === 'weight_tens_ones'
        ? 2
        : 1;
  const dual_right_digits =
    readout_split === 'imperial_from_cm' || readout_split === 'cm_slash_imperial'
      ? 2
      : readout_split === 'weight_tens_ones'
        ? 1
        : 1;

  const field_line_value = useDerivedValue(() => {
    if (!is_field) {
      return 0;
    }
    const n = lines_for_worklet;
    const p = user_progress.value;
    const span = n > 1 ? n - 1 : 1;
    const line_index =
      n <= 1 ? 0 : Math.min(Math.max(0, Math.round(p * span)), n - 1);
    return value_min_w + line_index;
  }, [is_field, lines_for_worklet, value_min_w]);

  const animated_number = useDerivedValue(() => {
    if (is_field) {
      return field_line_value.value;
    }
    return Math.ceil(user_progress.value * DEMO_LINES_AMOUNT);
  }, [is_field]);

  const animated_readout_left = useDerivedValue(() => {
    const v = field_line_value.value;
    if (readout_split === 'imperial_from_cm' || readout_split === 'cm_slash_imperial') {
      const total_in = v > 0 ? Math.round(v / 2.54) : 0;
      return Math.floor(total_in / 12);
    }
    if (readout_split === 'weight_tens_ones') {
      return Math.floor(v / 10);
    }
    return 0;
  }, [readout_split]);

  const animated_readout_right = useDerivedValue(() => {
    const v = field_line_value.value;
    if (readout_split === 'imperial_from_cm' || readout_split === 'cm_slash_imperial') {
      const total_in = v > 0 ? Math.round(v / 2.54) : 0;
      const f = Math.floor(total_in / 12);
      return total_in - f * 12;
    }
    if (readout_split === 'weight_tens_ones') {
      return v - Math.floor(v / 10) * 10;
    }
    return 0;
  }, [readout_split]);

  /** Collapse inches + ″ when whole feet (avoid `5′0″` / `5′ ″`). */
  const imperial_inches_cluster_style = useAnimatedStyle(() => {
    const show =
      readout_split === 'imperial_from_cm' || readout_split === 'cm_slash_imperial'
        ? Math.round(animated_readout_right.value) > 0
        : true;
    return {
      opacity: show ? 1 : 0,
      maxWidth: show ? 500 : 0,
      overflow: 'hidden' as const,
      flexDirection: 'row' as const,
      alignItems: 'baseline' as const,
      flexShrink: 0,
    };
  }, [readout_split]);

  const lines_amount_ref = useRef(lines_amount);
  const value_min_ref = useRef(value_min);
  lines_amount_ref.current = lines_amount;
  value_min_ref.current = value_min;

  /**
   * Progress updates stay on the UI thread for the live readout.
   * Do NOT push every tick to React — that re-renders the parent, rebuilds the pan
   * gesture mid-fling, and stacks digit animations until the app crashes.
   */
  const on_progress = useCallback((slider_progress: number) => {
    'worklet';
    if (slider_progress < 0) {
      return;
    }

    const n = lines_for_worklet;
    const span = n > 1 ? n - 1 : 1;
    const current_line_index =
      n <= 1 ? 0 : Math.min(Math.max(0, Math.round(slider_progress * span)), n - 1);

    if (current_line_index !== previous_line_index.value) {
      previous_line_index.value = current_line_index;
      /** Throttle haptics while flinging — avoids JS/bridge storms on long rulers. */
      const now = Date.now();
      if (now - last_haptic_ms.value >= 45) {
        last_haptic_ms.value = now;
        runOnJS(triggerSelectionFeedback)();
      }
    }
    user_progress.value = slider_progress;
  }, [lines_for_worklet, previous_line_index, last_haptic_ms, user_progress]);

  const commit_field_value_from_progress = useCallback(() => {
    if (!is_field) {
      return;
    }
    const n = lines_amount_ref.current;
    const vmin = value_min_ref.current;
    const span = n > 1 ? n - 1 : 1;
    const p = user_progress.value;
    const line_index = n <= 1 ? 0 : Math.min(Math.max(0, Math.round(p * span)), n - 1);
    const committed = snap_value_to_step(vmin + line_index);
    run_field_value(committed);
    user_progress.value = progress_from_value(committed);
  }, [is_field, progress_from_value, run_field_value, snap_value_to_step, user_progress]);

  const on_slider_gesture_active_ref = useRef(onSliderGestureActiveChange);
  on_slider_gesture_active_ref.current = onSliderGestureActiveChange;

  const on_gesture_active_change = useCallback(
    (active: boolean) => {
      gesture_active_ref.current = active;
      setGestureActive(active);
      on_slider_gesture_active_ref.current?.(active);
      if (!active && is_field) {
        commit_field_value_from_progress();
      }
    },
    [commit_field_value_from_progress, is_field],
  );

  const should_dual_row_start = is_readout_start && !is_readout_between;
  const has_trailing_readout = trailingReadout != null && trailingReadout !== false;

  const readout_lead_out =
    readoutLabelNudgeYProp > 0
      ? has_leading_readout
        ? <View style={readout_label_nudge_style}>{leadingReadout}</View>
        : null
      : leadingReadout;

  const readout_trail_out =
    readoutLabelNudgeYProp > 0
      ? has_trailing_readout
        ? <View style={readout_label_nudge_style}>{trailingReadout}</View>
        : null
      : trailingReadout;

  const readout_count_block =
    simpleReadout && readout_split === 'none' ? (
      <PlainAnimatedNumber
        count={animated_number}
        style={[
          {
            color: countColor,
            fontSize: resolved_font,
            fontWeight: '800',
            fontVariant: ['tabular-nums'],
            lineHeight: textDigitHeight,
            minHeight: textDigitHeight,
            textAlign: 'center',
            includeFontPadding: false,
            writingDirection: 'ltr',
          },
          Platform.OS === 'android' ? { textAlignVertical: 'center' as const } : null,
        ]}
      />
    ) : readout_split === 'none' ? (
      <AnimatedCount
        count={animated_number}
        maxDigits={maxDigits ?? count_digits}
        textDigitWidth={textDigitWidth}
        textDigitHeight={textDigitHeight}
        fontSize={resolved_font}
        color={countColor}
        gradientAccentColor={gradientAccentColor}
      />
    ) : readout_split === 'cm_slash_imperial' ? (
      <PlainCmSlashImperialReadout
        cm={animated_number}
        metricUnitLabel={metricUnitLabel ?? 'cm'}
        numberStyle={{
          color: countColor,
          fontSize: resolved_font,
          fontWeight: '800',
          fontVariant: ['tabular-nums'],
          lineHeight: textDigitHeight,
          minHeight: textDigitHeight,
          textAlign: 'center',
          includeFontPadding: false,
          writingDirection: 'ltr',
          ...(Platform.OS === 'android' ? { textAlignVertical: 'center' as const } : null),
        }}
        unitStyle={[
          S.metricUnitLabel,
          {
            color: bigLineColor ?? countColor ?? '#000',
            fontSize: imperial_glyph_size,
            writingDirection: isRTL() ? 'rtl' : 'ltr',
          },
        ]}
        slashStyle={[
          S.slashSep,
          {
            color: bigLineColor ?? countColor ?? '#000',
            fontSize: imperial_glyph_size,
            writingDirection: 'ltr',
          },
        ]}
        markStyle={[
          S.imperialTick,
          {
            color: bigLineColor ?? countColor ?? '#000',
            fontSize: resolved_font,
            lineHeight: textDigitHeight,
          },
        ]}
      />
    ) : (
      <View style={[S.readoutDualRow, should_dual_row_start && S.readoutDualRowStart, layoutLtrStyle]}>
        <AnimatedCount
          count={animated_readout_left}
          maxDigits={dual_left_digits}
          textDigitWidth={textDigitWidth}
          textDigitHeight={textDigitHeight}
          fontSize={resolved_font}
          color={countColor}
          gradientAccentColor={gradientAccentColor}
        />
        {readout_split === 'imperial_from_cm' ? (
          <Text
            style={[
              S.imperialTick,
              {
                color: bigLineColor ?? countColor ?? '#000',
                fontSize: resolved_font,
                lineHeight: textDigitHeight,
              },
            ]}
            allowFontScaling={false}>
            ′
          </Text>
        ) : (
          <View style={S.dualGap} />
        )}
        {readout_split === 'imperial_from_cm' ? (
          <Animated.View style={imperial_inches_cluster_style}>
            <AnimatedCount
              count={animated_readout_right}
              maxDigits={dual_right_digits}
              textDigitWidth={textDigitWidth}
              textDigitHeight={textDigitHeight}
              fontSize={resolved_font}
              color={countColor}
              gradientAccentColor={gradientAccentColor}
            />
            <Text
              style={[
                S.imperialQuote,
                {
                  color: bigLineColor ?? countColor ?? '#000',
                  fontSize: resolved_font,
                  lineHeight: textDigitHeight,
                },
              ]}
              allowFontScaling={false}>
              ″
            </Text>
          </Animated.View>
        ) : (
          <AnimatedCount
            count={animated_readout_right}
            maxDigits={dual_right_digits}
            textDigitWidth={textDigitWidth}
            textDigitHeight={textDigitHeight}
            fontSize={resolved_font}
            color={countColor}
            gradientAccentColor={gradientAccentColor}
          />
        )}
      </View>
    );

  const readout_value_cluster = (
    <View style={[S.readoutValueCluster, layoutLtrStyle, readoutValueClusterStyle]}>
      {readout_count_block}
      {readout_trail_out}
    </View>
  );

  return (
    <View
      testID={testID}
      style={[
        S.container,
        { backgroundColor, minHeight },
        !fillContainer && embed_style,
        fillContainer && S.fill,
        !fillContainer && is_readout_stretch && S.containerReadoutStart,
        containerStyle,
      ]}>
      <View
        style={[
          simpleReadout ? S.readoutRowSimple : S.readoutRow,
          is_readout_start && !is_readout_between && S.readoutRowStart,
          is_readout_between && (has_leading_readout ? S.readoutRowBetween : S.readoutRowBetweenLone),
          layoutLtrStyle,
          readoutGroupStyle,
          gesture_active ? S.readoutRowActive : null,
          readoutGutterV != null && { marginBottom: readoutGutterV },
        ]}>
        {is_readout_between ? (
          <>
            {has_leading_readout && <View style={S.readoutLeadInBetween}>{readout_lead_out}</View>}
            {readout_value_cluster}
          </>
        ) : (
          <>
            {readout_lead_out}
            {readout_count_block}
            {readout_trail_out}
          </>
        )}
      </View>
      {captionBelowReadout != null && captionBelowReadout !== false ? (
        <View style={[S.captionSlot, layoutLtrStyle, is_readout_stretch && S.captionSlotStart]}>
          {captionBelowReadout}
        </View>
      ) : null}

      <DraggableSlider
        scrollableAreaHeight={scrollableAreaHeight}
        lineCanvasPaddingTop={line_canvas_pad}
        spacePerLine={slider_space_per_line}
        bigLineIndexOffset={big_line_every}
        lineValueBase={is_field ? value_min : 0}
        snapEach={1}
        linesAmount={lines_amount}
        maxLineHeight={maxLineHeight}
        minLineHeight={minLineHeight}
        lineWidth={lineWidth}
        onProgressChange={on_progress}
        lineColor={lineColor}
        bigLineColor={bigLineColor}
        initialProgress={initial_progress}
        rulerStopsMode={is_field}
        idleArrowsAfterMs={idleSliderArrowsAfterMs}
        idleArrowsColor={idleSliderArrowsColor}
        markerColor={scaleMarkerColor}
        onGestureActiveChange={on_gesture_active_change}
        snapSpring={snapSpring}
        overscrollTicks={overscrollTicks}
        isRTL={false}
      />

      {showExpandButton ? (
        <Pressable style={S.button} onPress={() => setIsExpanded(prev => !prev)}>
          <SvgIcon name={!is_expanded ? 'arrow-up' : 'arrow-down'} size={28} color="#ffffff" />
        </Pressable>
      ) : null}
    </View>
  );
};

const S = StyleSheet.create({
  button: {
    alignItems: 'center',
    aspectRatio: 1,
    backgroundColor: '#111',
    borderRadius: 32,
    bottom: 48,
    height: 64,
    justifyContent: 'center',
    position: 'absolute',
    right: 32,
  },
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  fill: { flex: 1 },
  embed: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'flex-start',
    paddingTop: 2,
    paddingBottom: 4,
    width: '100%',
  },
  readoutRow: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    gap: androidScaleSize(6),
    paddingHorizontal: androidScaleSize(8),
    width: '100%',
  },
  readoutRowSimple: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    gap: androidScaleSize(8),
    paddingHorizontal: androidScaleSize(8),
    width: '100%',
  },
  readoutRowActive: {
    opacity: 0.96,
  },
  readoutRowStart: {
    alignItems: 'baseline',
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    width: '100%',
  },
  readoutRowBetween: {
    alignItems: 'baseline',
    flexWrap: 'nowrap',
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    gap: androidScaleSize(8),
    width: '100%',
  },
  readoutRowBetweenLone: {
    alignItems: 'baseline',
    alignSelf: 'stretch',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    width: '100%',
  },
  readoutValueCluster: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: androidScaleSize(6),
  },
  readoutLeadInBetween: {
    flexDirection: 'row',
    flex: 1,
    minWidth: 0,
    alignItems: 'baseline',
  },
  readoutDualRow: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexShrink: 0,
    justifyContent: 'center',
    gap: 0,
  },
  readoutDualRowStart: {
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
  },
  containerReadoutStart: {
    alignItems: 'stretch',
    alignSelf: 'stretch',
    width: '100%',
  },
  embedStart: {
    alignItems: 'stretch',
    alignSelf: 'stretch',
  },
  dualGap: {
    width: androidScaleSize(2),
    flexShrink: 0,
  },
  imperialTick: {
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    includeFontPadding: false,
    marginHorizontal: androidScaleSize(1),
  },
  imperialQuote: {
    fontWeight: '800',
    includeFontPadding: false,
    fontVariant: ['tabular-nums'],
    marginStart: androidScaleSize(1),
  },
  metricUnitLabel: {
    fontWeight: '700',
    includeFontPadding: false,
    marginStart: androidScaleSize(4),
    marginEnd: androidScaleSize(4),
  },
  slashSep: {
    fontWeight: '600',
    includeFontPadding: false,
    marginHorizontal: androidScaleSize(4),
  },
  captionSlot: {
    alignItems: 'center',
    marginTop: 2,
    maxWidth: '100%',
    width: '100%',
  },
  captionSlotStart: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
});
